/**
 * リザルト(docs/02_game_design.md §1 / §5.5-§5.7)。
 *
 * スコア・正確率・速度・星・コイン獲得を出し、リトライ / ステージ選択へ戻る。
 * コイン内訳は「クリア報酬(スコア5%)」と「初回★ボーナス」を分けて見せる。
 *
 * 演出(docs/15 §2):★は1つずつポン・ポンと入り(`Stars animated`)、
 * 星がついたら まわりに星が弾ける(§2B)。
 * つぎの操作は キーだけでも選べる — Enter =「もういちど」/ Esc = もどる(§2A)。
 */

import { TYPING_DOMAIN } from '../services/activityLog';
import { isStageGraduated } from '../logic/unlock';
import { useGameStore } from '../store/gameStore';
import { KeyBadge, StarBurst, useHotkey } from './components/gameFeel';
import { Stars } from './components/Stars';
import { TutorBubble } from './components/TutorBubble';
import { useRecordActivity } from './useRecordActivity';
import { useTutorResult } from './useTutorResult';

const DIFFICULTY_LABELS: Readonly<Record<string, string>> = {
  easy: 'かんたん',
  normal: 'ふつう',
  hard: 'むずかしい',
};

/**
 * きろくの保存状態のおしらせ(小学生向けにひらがな主体・控えめに)。
 * `saved` のときは何も出さない(ふだんは静かに)。
 */
const SAVE_NOTICES: Readonly<Record<string, string>> = {
  saving: 'きろくを ほぞんしてるよ。おわるまで まってね',
  pending: 'いまは つうしんが できないので、あとで きろくを おくるよ',
  rejected: 'きろくを ほぞんできませんでした。おうちのひとに おしえてね',
};

/** 復旧前・旧版の結果は安全に再送できないため、更新してから遊び直してもらう。 */
const UPDATE_REQUIRED_REASONS = new Set(['recovery-required', 'result-id-required']);
const UPDATE_REQUIRED_NOTICE =
  'きろくを ほぞんできなかったよ。このページを よみこみなおしてから もういちど あそんでね';

export function ResultScreen() {
  const result = useGameStore((state) => state.result);
  const practice = useGameStore((state) => state.practice);
  const progress = useGameStore((state) => state.progress);
  const stageAccessStatus = useGameStore((state) => state.stageAccessStatus);
  const stageError = useGameStore((state) => state.stageError);
  const retry = useGameStore((state) => state.retry);
  const backToStageSelect = useGameStore((state) => state.backToStageSelect);

  /*
   * パルの体色のもとになる「タイピングを1回あそんだ」の記録(docs/14 §2)。
   * にがてキー練習も**がんばりには数える**(★・コインは付かないが、
   * 取り組んだ事実は色に出したい)。フックなので早期 return より前に呼ぶ。
   */
  useRecordActivity(result ? TYPING_DOMAIN : null);

  /*
   * キーだけで次へ進める(docs/15 §2A)。Enter は「もういちど」、Esc は「もどる」。
   * ボタンにフォーカスがあるときの Enter は `<button>` にまかせる(共通フックの中で判定)。
   */
  const graduated =
    result !== null &&
    (isStageGraduated(result.stage.id, result.difficulty, progress) ||
      result.receipt.graduated === true);
  const retryEnabled =
    result !== null &&
    result.saveStatus !== 'saving' &&
    stageAccessStatus !== 'loading' &&
    !graduated;
  useHotkey('Enter', () => void retry(), retryEnabled);
  useHotkey('Escape', backToStageSelect, result !== null);

  /*
   * パルの ひとこと(docs/07 §7.2)。**リザルトの表示はブロックしない**
   * — 点数と★を先に出して、返事が来たら下に差し込む。
   * 取れなければ何も出さない(ローカルモード・通信断)。
   * 送るのは数値だけ(自由文字列はそもそも型に無い)。
   */
  const tutorReply = useTutorResult(
    result
      ? {
          module: 'typing',
          stars: result.receipt.stars,
          missRate: Math.round((1 - result.result.accuracy) * 1000) / 1000,
          kpm: Math.round(result.result.kpm),
        }
      : null,
  );

  if (!result) {
    return (
      <div className="screen result">
        <p className="loading-text">…</p>
        <button type="button" className="big-button" onClick={backToStageSelect}>
          ステージせんたくへ
        </button>
      </div>
    );
  }

  const { stage, difficulty, result: session, receipt, saveStatus } = result;
  // すでに卒業済みだった場合のサーバー拒否は、通信失敗とは分けて伝える。
  const graduationRejected = receipt.reason === 'stage-graduated';
  const updateRequired = UPDATE_REQUIRED_REASONS.has(receipt.reason ?? '');
  const saveNotice = graduationRejected
    ? null
    : updateRequired
      ? UPDATE_REQUIRED_NOTICE
      : (SAVE_NOTICES[saveStatus] ?? null);
  const accuracy = Math.round(session.accuracy * 1000) / 10;
  const kpm = Math.round(session.kpm);
  const seconds = Math.round(session.durationMs / 100) / 10;

  return (
    <div className={`screen result ${session.cleared ? 'cleared' : 'failed'}`}>
      <h1 className="screen-title">
        {graduated ? 'そつぎょう!' : session.cleared ? 'クリア!' : 'ざんねん…'}
      </h1>
      <p className="result-stage">
        {stage.title} / {DIFFICULTY_LABELS[difficulty] ?? difficulty}
      </p>

      {/* ★は1つずつポン・ポンと入る(`animated`)。docs/15 §2B。 */}
      <Stars value={receipt.stars} size="lg" animated />
      {/*
       * 星がついた回だけ弾けさせる(星0では祝わない)。
       * 高さを持たない部品なので、差し込んでもレイアウトはずれない。
       */}
      {session.cleared && receipt.stars > 0 ? <StarBurst stars={receipt.stars} /> : null}

      <dl className="result-grid">
        <div className="result-item big">
          <dt>スコア</dt>
          <dd>
            {session.score}
            {receipt.isNewBestScore ? <span className="badge new">ベスト!</span> : null}
          </dd>
        </div>
        <div className="result-item">
          <dt>せいかくりつ</dt>
          <dd>{accuracy}%</dd>
        </div>
        <div className="result-item">
          <dt>はやさ</dt>
          <dd>{kpm} だ/ぷん</dd>
        </div>
        <div className="result-item">
          <dt>さいだいコンボ</dt>
          <dd>{session.maxCombo}</dd>
        </div>
        <div className="result-item">
          <dt>うった かず</dt>
          <dd>
            {session.correctKeys} <span className="miss">ミス {session.missKeys}</span>
          </dd>
        </div>
        <div className="result-item">
          <dt>じかん</dt>
          <dd>{seconds} びょう</dd>
        </div>
      </dl>

      {practice ? (
        // にがてキー練習は進捗に残さない(docs/07_learning_support.md §4.2)。
        <p className="coin-breakdown">れんしゅうモード: ★・コインは つかないよ</p>
      ) : (
        <div className="coin-result">
          <p className="coin-total">
            <span className="coin-icon">◎</span> +{receipt.coinsEarned} コイン
          </p>
          <p className="coin-breakdown">
            クリア {receipt.clearCoins}
            {receipt.starBonusCoins > 0 ? ` / はじめての★ボーナス ${receipt.starBonusCoins}` : ''}
            {' / '}
            もっている {receipt.balance}
          </p>
          {/*
           * がんばりポイント(docs/12 §2 のリーグ軸)。**コインとは別の指標**なので
           * 行を分けて控えめに出す。サーバーから返ってきたときだけ
           * (ローカルモード・オフラインでは出さない)。
           */}
          {typeof receipt.effortEarned === 'number' && receipt.effortEarned > 0 ? (
            <p className="effort-earned">
              <span className="effort-icon" aria-hidden="true">
                🏅
              </span>{' '}
              +{receipt.effortEarned} がんばりポイント
            </p>
          ) : null}
        </div>
      )}

      {saveNotice ? (
        <p className="save-notice" role="status">
          {saveNotice}
        </p>
      ) : null}

      {stageAccessStatus === 'error' && stageError ? (
        <p className="save-notice" role="alert">
          {stageError}
        </p>
      ) : null}

      {graduated ? (
        <p className="graduation-result" role="status">
          {graduationRejected
            ? '🏅 この むずかしさは そつぎょうずみだよ。べつの むずかしさ や ほかの ことばに ちょうせんしよう。'
            : '🏅 そつぎょう！ べつの むずかしさ や ほかの ことばに ちょうせんしよう。'}
        </p>
      ) : null}

      {/* パルの ひとこと(取れたときだけ。失敗時は枠ごと出さない)。 */}
      {tutorReply ? (
        <div className="result-tutor">
          <TutorBubble reply={tutorReply} size="sm" />
        </div>
      ) : null}

      {/*
       * キーバッジは `aria-hidden`(共通の KeyBadge)。読み上げの名前は
       * 「もういちど」のままにしておきたいので、文言には混ぜない。
       */}
      <div className="result-buttons">
        <button
          type="button"
          className="big-button retry"
          onClick={() => void retry()}
          disabled={!retryEnabled}
        >
          {stageAccessStatus === 'error' ? 'もういちど ためす' : 'もういちど'}
          <KeyBadge keyLabel="Enter" className="on-dark" />
        </button>
        <button type="button" className="big-button back" onClick={backToStageSelect}>
          {practice ? 'きろくへ もどる' : 'ステージせんたくへ'}
          <KeyBadge keyLabel="Esc" className="on-dark" />
        </button>
      </div>
      <p className="gf-key-hint">
        {retryEnabled ? 'Enter で もういちど / Esc で もどる' : 'Esc で もどる'}
      </p>
    </div>
  );
}
